/**
 * useAnalytics.ts
 *
 * Composable for tracking user events with Umami analytics
 * Provides a centralized way to track important user interactions
 */

export interface AnalyticsEventData {
  [key: string]: string | number | boolean | undefined
}

// Zoekvelden roepen de track-functie bij elke toetsaanslag aan; per event-naam
// alleen de laatste waarde na een korte pauze versturen.
const SEARCH_DEBOUNCE_MS = 1500
const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>()

export function useAnalytics() {
  const trackEvent = (eventName: string, eventData?: AnalyticsEventData) => {
    const umami = window.umami
    if (!umami) return
    try {
      // Umami v2 (umami.timblox.nl) exposeert alleen `track`; `trackEvent` is de v1-API.
      if (typeof umami.track === 'function') {
        umami.track(eventName, eventData)
      } else if (typeof umami.trackEvent === 'function') {
        umami.trackEvent(eventName, eventData)
      }
    } catch (error) {
      console.warn('Failed to track event:', eventName, error)
    }
  }

  const trackEventDebounced = (eventName: string, eventData?: AnalyticsEventData) => {
    const existing = debounceTimers.get(eventName)
    if (existing) clearTimeout(existing)
    debounceTimers.set(
      eventName,
      setTimeout(() => {
        debounceTimers.delete(eventName)
        trackEvent(eventName, eventData)
      }, SEARCH_DEBOUNCE_MS)
    )
  }

  // Authentication events
  const trackLogin = () => {
    trackEvent('user_login')
  }

  const trackLogout = () => {
    trackEvent('user_logout')
  }

  // Timesheet events
  const trackTimesheetSubmit = (week: number, year: number, entriesCount?: number) => {
    trackEvent('timesheet_submit', { week, year, entriesCount })
  }

  const trackTimesheetSaveDraft = (week: number) => {
    trackEvent('timesheet_save_draft', { week })
  }

  const trackTimesheetEdit = (week: number, entryType?: string) => {
    trackEvent('timesheet_edit', { week, entryType })
  }

  // Week analysis
  const trackWeekAnalysisRequested = (week: number, year: number, historicalWeeks?: number) => {
    trackEvent('week_analysis_requested', { week, year, historicalWeeks })
  }

  const trackWeekAnalysisRefreshed = (week: number, year: number) => {
    trackEvent('week_analysis_refreshed', { week, year })
  }

  // Leave requests
  const trackLeaveRequestCreated = (leaveType?: string, days?: number) => {
    trackEvent('leave_request_created', { leaveType, days })
  }

  const trackLeaveRequestSubmitted = (leaveType?: string) => {
    trackEvent('leave_request_submitted', { leaveType })
  }

  // Search & Filter events
  // Zoekteksten zijn vaak namen van collega's (PII) - nooit de tekst zelf versturen,
  // alleen de lengte.
  const trackPeopleSearch = (query: string, resultCount?: number) => {
    trackEventDebounced('people_search', { queryLength: query.length, resultCount })
  }

  const trackProjectSearch = (query: string, resultCount?: number) => {
    trackEventDebounced('project_search', { queryLength: query.length, resultCount })
  }

  const trackFilterApplied = (filterType: string, filterValue?: string) => {
    if (filterType === 'search') {
      trackEventDebounced('filter_applied', { filterType, queryLength: filterValue?.length ?? 0 })
      return
    }
    trackEvent('filter_applied', { filterType, filterValue: filterValue?.substring(0, 50) })
  }

  // Projectdetailpagina (tabs, help-venster en weergave van de trendgrafiek)
  const trackProjectDetailTabChanged = (tab: string) => {
    trackEvent('project_detail_tab_changed', { tab })
  }

  const trackProjectTrendViewChanged = (mode: string) => {
    trackEvent('project_trend_view_changed', { mode })
  }

  // Detail views
  const trackPersonDetailsViewed = (personId?: string) => {
    trackEvent('person_details_viewed', personId ? { personId } : undefined)
  }

  const trackProjectDetailsViewed = (projectId?: string) => {
    trackEvent('project_details_viewed', projectId ? { projectId } : undefined)
  }

  // Organogram
  const trackOrgChartNavigated = (departmentId?: string, depth?: number) => {
    trackEvent('org_chart_navigated', { departmentId, depth })
  }

  // UI Settings
  const trackThemeToggled = (theme: 'light' | 'dark') => {
    trackEvent('theme_toggled', { theme })
  }

  const trackLanguageSwitched = (language: string) => {
    trackEvent('language_switched', { language })
  }

  // Feature discovery
  const trackChangelogViewed = () => {
    trackEvent('changelog_viewed')
  }

  // Help: ?-vensters op pagina's (source = pagina) en het helpcentrum
  const trackHelpDialogOpened = (source: string, article: string) => {
    trackEvent('help_dialog_opened', { source, article })
  }

  const trackHelpDialogArticleChanged = (source: string, article: string) => {
    trackEvent('help_dialog_article_changed', { source, article })
  }

  const trackHelpArticleViewed = (article: string, via: 'browse' | 'search' | 'link' | 'dialog') => {
    trackEvent('help_article_viewed', { article, via })
  }

  // Nooit de zoektekst zelf versturen; gedebounced omdat het bij elke toetsaanslag wordt aangeroepen.
  const trackHelpSearch = (queryLength: number, resultCount: number) => {
    trackEventDebounced('help_search', { queryLength, resultCount })
  }

  const trackHelpFeedback = (article: string, helpful: boolean) => {
    trackEvent('help_feedback', { article, helpful })
  }

  // Feedback: alleen tellingen, nooit de tekst of naam (die staan in vicoma-api)
  const trackFeedbackSubmitted = (category: string) => {
    trackEvent('feedback_submitted', { category })
  }

  const trackFeatureVote = (featureId: string) => {
    trackEvent('feature_vote', { featureId })
  }

  // Grid interactions
  const trackGridPageSizeChanged = (pageSize: number) => {
    trackEvent('grid_page_size_changed', { pageSize })
  }

  const trackGridSortApplied = (column: string, direction: 'asc' | 'desc') => {
    trackEvent('grid_sort_applied', { column: column.substring(0, 50), direction })
  }

  // Generic export for custom events
  const trackCustom = (eventName: string, data?: AnalyticsEventData) => {
    trackEvent(eventName, data)
  }

  return {
    // Generic
    trackEvent,
    trackCustom,

    // Authentication
    trackLogin,
    trackLogout,

    // Timesheet
    trackTimesheetSubmit,
    trackTimesheetSaveDraft,
    trackTimesheetEdit,

    // Analysis
    trackWeekAnalysisRequested,
    trackWeekAnalysisRefreshed,

    // Leave
    trackLeaveRequestCreated,
    trackLeaveRequestSubmitted,

    // Search & Filter
    trackPeopleSearch,
    trackProjectSearch,
    trackFilterApplied,

    // Project detail
    trackProjectDetailTabChanged,
    trackProjectTrendViewChanged,

    // Detail views
    trackPersonDetailsViewed,
    trackProjectDetailsViewed,

    // Organogram
    trackOrgChartNavigated,

    // Settings
    trackThemeToggled,
    trackLanguageSwitched,

    // Features
    trackChangelogViewed,
    trackHelpDialogOpened,
    trackHelpDialogArticleChanged,
    trackHelpArticleViewed,
    trackHelpSearch,
    trackHelpFeedback,
    trackFeedbackSubmitted,
    trackFeatureVote,

    // Grid
    trackGridPageSizeChanged,
    trackGridSortApplied,
  }
}
